import { E2E_COMPANY_SLUG_OVERRIDE_KEY } from '../exports/Constants';
import { FranchiseResolverParams } from '../exports/Types';
import StorefrontResource from './StorefrontResource';

const whiteListHosts = ['localhost', '10.0.2.2', '127.0.0.1', '0.0.0.0'];

// Reads the sessionStorage key an e2e run may set to pick which seeded company a localhost build
// serves.
//
// The slug is normally inlined at BUILD time, so covering a second company meant booting a second
// dev server on its own port — one process per company, forever. This override collapses that to a
// per-test choice.
//
// Gated on VITE_EXPOSE_REDUX_STORE, which is set ONLY by playwright.config.ts's webServer env and
// is absent from every committed .env — the same gate that already decides whether the Redux store
// is handed to window.store. In a real bundle the whole branch is unreachable, and the
// `whiteListHosts` check at the call site means it could not apply to a deployed hostname even if
// it were.
const readE2eCompanySlugOverride = (): string | null => {
  if (import.meta.env.VITE_EXPOSE_REDUX_STORE !== 'true') {
    return null;
  }

  // sessionStorage throws on access under some privacy settings, and this runs before the first
  // paint (the resolver is consulted on the very first backend call) — an exception here would take
  // down app boot rather than degrade.
  try {
    return window.sessionStorage.getItem(E2E_COMPANY_SLUG_OVERRIDE_KEY) || null;
  } catch {
    return null;
  }
};

class DomainResource {
  /**
   * The params every franchise-scoped consumer request carries so the backend can resolve WHICH
   * franchise is being served.
   *
   * A deployed bundle sends the hostname it is served on, verbatim. There is deliberately no
   * client-side list of "known" root domains and no www./case normalisation here: the backend owns
   * the `shop_domains` registry and normalises the host, so one bundle serves every root
   * (online-karte.com, order-up.co, online-orders.com, a partner's own root, a franchise's custom
   * FQDN) without a rebuild.
   *
   * localhost has no subdomain to resolve, so the whitelisted dev/e2e hosts fall back to a slug —
   * the e2e sessionStorage override first, then the build-time VITE_DEFAULT_LOCAL_SUBDOMAIN. A
   * storefront build (VITE_CONFIG_BASE_DOMAIN set) always resolves by hostname, as it does today.
   */
  public getFranchiseResolverParams(): FranchiseResolverParams {
    const hostname = window.location.hostname;

    if (StorefrontResource.isStorefront()) {
      return { hostname };
    }

    if (whiteListHosts.includes(hostname)) {
      const slug = readE2eCompanySlugOverride() ?? import.meta.env.VITE_DEFAULT_LOCAL_SUBDOMAIN;

      return slug ? { franchise_slug: slug } : { hostname };
    }

    return { hostname };
  }

  public getHostname(): string {
    return window.location.hostname.toLowerCase().replace(/^www\./, '');
  }
}

export default new DomainResource();
